import type { Template } from "@livereader/graphly-d3";
import type { Graph, KnowledgeObject } from "@lr-notitia/notitia-engineer/schemas";
import { api } from "@lr-notitia/notitia-engineer/client";
import { ref } from "vue";
import { defineStore } from "pinia";
import { newGraph, handleGraphModification } from "./graph/utils";
import { datatypes, datatype } from "./graph/datatype";
import { enumItems, enumItem } from "./graph/enumItem";

import { conceptDataobjects, conceptDataobject } from "./graph/c-dataobject";
import { conceptAttributes, conceptAttribute } from "./graph/c-attribute";
import { conceptFragments, conceptFragment } from "./graph/c-fragment";
import { conceptFuncs, conceptFunc } from "./graph/c-function";
import { conceptEmitters, conceptEmitter } from "./graph/c-emitter";
import { conceptEvents, conceptEvent } from "./graph/c-event";
import { conceptTasks, conceptTask } from "./graph/c-task";
import { conceptViews, conceptView } from "./graph/c-view";

import { templateProcesses, templateProcess } from "./graph/t-process";
import { templateDataobjects, templateDataobject } from "./graph/t-dataobject";
import { templateTasks, templateTask } from "./graph/t-task";
import { templateEvents, templateEvent } from "./graph/t-event";
import { templateWorkspaces, templateWorkspace } from "./graph/t-workspace";

export const useGraphStore = defineStore("graph", () => {
	const domainId = ref("");
	const graph = ref<Graph>(newGraph());
	const templates: { [key: string]: Template } = {};

	const DEFAULT_GRAPHLY_SHAPE = "https://cdn.graphly.dev/@livereader/base/1.0.0";

	function loadGraph(dId: string): Promise<Graph> {
		domainId.value = dId;
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId.value)
				.graph()
				.get("wip")
				.then((data) => {
					graph.value = data;
					resolve(data);
					api()
						.socket()
						.domain(domainId.value)
						.graph()
						.on((message) => handleGraphModification(graph, message.data));
				})
				.catch(() => reject("Failed to load graph"));
		});
	}
	function patchGraph(id: string, knowledgeObject: KnowledgeObject) {
		api()
			.domain(domainId.value)
			.graph()
			.patch(id, knowledgeObject)
			.then((data) => handleGraphModification(graph, data));
	}
	function clearGraph() {
		api().socket().domain(domainId.value).graph().off();
		graph.value = newGraph();
		domainId.value = "";
	}

	function graphlyTemplate(url: string): Promise<Template> {
		return new Promise((resolve, reject) => {
			if (templates[url]) return resolve(templates[url]);
			import(/* @vite-ignore */ url)
				.then((module) => {
					const template = module.default;
					if (!template) return reject("Failed to load graphly template");
					resolve(template);
				})
				.catch(() => reject("Failed to load graphly template"));
		});
	}

	return {
		graph,
		loadGraph,
		patchGraph,
		clearGraph,
		graphlyTemplate,
		DEFAULT_GRAPHLY_SHAPE,
		datatypes: datatypes(graph),
		datatype: datatype(domainId, graph),
		enumItems: enumItems(graph),
		enumItem: enumItem(domainId, graph),
		concept: {
			dataobjects: conceptDataobjects(graph),
			dataobject: conceptDataobject(domainId, graph),
			attributes: conceptAttributes(graph),
			attribute: conceptAttribute(domainId, graph),
			fragments: conceptFragments(graph),
			fragment: conceptFragment(domainId, graph),
			functions: conceptFuncs(graph),
			function: conceptFunc(domainId, graph),
			emitters: conceptEmitters(graph),
			emitter: conceptEmitter(domainId, graph),
			events: conceptEvents(graph),
			event: conceptEvent(domainId, graph),
			tasks: conceptTasks(graph),
			task: conceptTask(domainId, graph),
			views: conceptViews(graph),
			view: conceptView(domainId, graph),
		},
		template: {
			processes: templateProcesses(graph),
			process: templateProcess(domainId, graph),
			dataobjects: templateDataobjects(graph),
			dataobject: templateDataobject(domainId, graph),
			tasks: templateTasks(graph),
			task: templateTask(domainId, graph),
			events: templateEvents(graph),
			event: templateEvent(domainId, graph),
			workspaces: templateWorkspaces(graph),
			workspace: templateWorkspace(domainId, graph),
		},
	};
});
